import * as React from "react";
import {onError} from "@apollo/client/link/error";
import {ApolloClient, ApolloLink, ApolloProvider, createHttpLink, from, InMemoryCache} from '@apollo/client';
import {useRecoilState} from "recoil";
import {CurrentUserState} from "./atoms/CurrentUser";
import {AccessController} from "./AccessController";
import MainWindow from "./MainWindow";
import {PubSub} from "./components/PubSub";
import {ErrorHandler} from "./ErrorHandler";
import {NavigationController} from "./navigation/NavigationController";
import {setContext} from "@apollo/client/link/context";
import {Route, Routes} from "react-router-dom";
import {InviteJoinPage} from "./pages/InviteJoinPage";
import {ForgotPasswordPage} from "./pages/ForgotPasswordPage";
import {ResetPasswordPage} from "./pages/ResetPasswordPage";
import {InviteAcceptPage} from "./pages/account/InviteAcceptPage";
import {createRetryLink} from "./ApolloRetry";
import {ShippingCostsPage} from "./pages/shippingcosts/ShippingCostsPage";
import TopBar from "./layout/TopBar";
import {ActiveAuditChangeSnackbar} from "./components/ActiveAuditChangeSnackbar";
import {getActiveAuditChange} from "./atoms/ActiveAuditChange";

export const cache = new InMemoryCache({});

/**
 * List of mutations that accept auditNoteId parameter.
 * The Apollo Link will automatically inject the active audit change ID into these mutations.
 */
const MUTATIONS_WITH_AUDIT_NOTE_ID = [
   'BrandLocalizationsUpdate',
   'BrandRuleActivation',
   'BrandRuleCreate',
   'BrandRuleDelete',
   'BrandRuleUpdate',
   'BrandSettingsUpdate',
   'BrickMailActivate',
   'BrickMailCopy',
   'BrickMailReset',
   'BrickMailReset2',
   'BrickMailUpdate',
];

/**
 * Extract mutation field names from a GraphQL operation.
 * Returns the actual mutation names being called (e.g., 'BrandSettingsUpdate'),
 * not the operation name (e.g., 'TradeInCountriesUpdate').
 */
const getMutationFieldNames = (operation: any): string[] => {
   const definitions = operation.query?.definitions || [];
   const mutationDef = definitions.find(
      (def: any) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
   );

   if (!mutationDef?.selectionSet?.selections) {
      return [];
   }

   return mutationDef.selectionSet.selections
      .filter((sel: any) => sel.kind === 'Field')
      .map((sel: any) => sel.name?.value)
      .filter(Boolean);
};

/**
 * Apollo Link that automatically injects auditNoteId into mutations that support it.
 */
const auditNoteLink = new ApolloLink((operation, forward) => {
   const mutationNames = getMutationFieldNames(operation);

   const needsAuditNoteId = mutationNames.some(name =>
      MUTATIONS_WITH_AUDIT_NOTE_ID.includes(name)
   );

   if (needsAuditNoteId) {
      const activeAuditChange = getActiveAuditChange();
      if (activeAuditChange?._id) {
         operation.variables = {
            ...operation.variables,
            auditNoteId: activeAuditChange._id
         };
      }
   }

   return forward(operation);
});

// @ts-ignore
const errorLink = onError(({graphQLErrors, networkError}) => {
   if (graphQLErrors) {
      if (graphQLErrors.find((error: any) => {
         return error.message && error.message.startsWith("Not Authorised");
      })) {
         PubSub.emit('logout');
      } else {
         PubSub.emit('errors', graphQLErrors);
      }
   }
});

const cleanTypeName = new ApolloLink((operation, forward) => {
   if (operation.variables) {
      const omitTypename = (key: any, value: any) => (key === '__typename' ? undefined : value);
      operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
   }
   return forward(operation).map((data) => {
      return data;
   });
});

const reactAppServer = import.meta.env.VITE_APISERVER || 'https://core.create2stay.com';

const client: ApolloClient<any> = new ApolloClient({
   cache,
   link: from([
      auditNoteLink,
      cleanTypeName,
      errorLink,
      setContext((request, previousContext) => {
         const accessToken = sessionStorage.getItem('accessToken');
         return {
            headers: {
               authorization: accessToken || '',
               pathname: window.location.pathname || ''
            }
         }
      }),
      createRetryLink(),
      createHttpLink({uri: reactAppServer + '/api/graphql'}),
   ]),
   credentials: 'include',
   defaultOptions: {
      query: {
         fetchPolicy: 'no-cache',
         errorPolicy: 'none',
      },
      mutate: {
         errorPolicy: 'none',
      },
   }
});

const PublicShippingCostsPage = () => {
   return <>
      <TopBar anchor="left" hideUserWidgets={true} />
      <div style={{paddingLeft: 20, paddingTop: 58, paddingRight: 20}}>
         <ShippingCostsPage />
      </div>
   </>;
};

export const ApolloApp = () => {
   return <ApolloProvider client={client}>
      <Routes>
         <Route path={"join/:token"} element={<InviteJoinPage/>}/>
         <Route path={"reset/:token"} element={<ResetPasswordPage/>}/>
         <Route path={"reset"} element={<ForgotPasswordPage/>}/>
         <Route path={"shippingcosts"} element={<PublicShippingCostsPage/>}/>
         <Route path={"*"} element={<AccessArea/>}/>
      </Routes>
   </ApolloProvider>
}

const AccessArea = () => {
   return <AccessController>
      <ErrorHandler>
         <Routes>
            <Route path={"account/accept"} element={<InviteAcceptPage/>}/>
            <Route path={"*"} element={<MainWindowArea/>}/>
         </Routes>
      </ErrorHandler>
   </AccessController>
}

const MainWindowArea = () => {
   const [currentUser] = useRecoilState(CurrentUserState);
   return <NavigationController shopId={currentUser?.shopId || undefined}>
      <div style={{paddingLeft: 20, paddingTop: 10, paddingRight: 20}}>
         <MainWindow/>
      </div>
      <ActiveAuditChangeSnackbar />
   </NavigationController>
}